import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X, User } from "lucide-react";
import Marquee from "react-fast-marquee";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { subscribeNewsletter } from "@/lib/api";
import { ACCOUNT } from "@/constants/testIds";
import { toast } from "sonner";

const LOGO = "/majnun-logo.png";

const NAV = [
  { to: "/shop", label: "Collections" },
  { to: "/events", label: "Events" },
  { to: "/story", label: "Manifesto" },
  { to: "/contact", label: "Contact" },
];

const Layout = ({ children }) => {
  const { count } = useCart();
  const { user, openAuthModal } = useAuth();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  const onAccountClick = () => {
    if (user) navigate("/settings");
    else openAuthModal();
  };

  useEffect(() => {
    setOpen(false);
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  const submitNewsletter = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      const res = await subscribeNewsletter(email);
      toast.success(res.message);
      setEmail("");
    } catch {
      toast.error("Something broke. Try again.");
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <header
        data-testid="site-header"
        className="sticky top-0 z-50 bg-background border-b border-border"
      >
        <div className="flex items-center justify-between px-5 md:px-10 h-16">
          <Link
            to="/"
            data-testid="logo-link"
            className="flex items-center"
          >
            <img src={LOGO} alt="MAJNUN" className="h-10 md:h-12 w-auto object-contain" />
          </Link>
          <nav className="hidden md:flex items-center gap-10">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={`text-xs font-mono uppercase tracking-[0.2em] transition-colors hover:text-foreground ${
                  location.pathname === n.to ? "text-foreground" : "text-muted-foreground"
                }`}
              >
                {n.label}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-5">
            <button
              data-testid={ACCOUNT.headerButton}
              onClick={onAccountClick}
              className="relative flex items-center"
              aria-label="Account"
            >
              {user && user.picture ? (
                <img
                  data-testid={ACCOUNT.headerAvatar}
                  src={user.picture}
                  alt=""
                  className="w-6 h-6 object-cover border border-border"
                />
              ) : (
                <User className="w-5 h-5" strokeWidth={1.5} />
              )}
              {user && (
                <span className="absolute -bottom-1 -right-1 w-1.5 h-1.5 bg-[#5B2A86] rounded-full" />
              )}
            </button>
            <Link to="/cart" data-testid="cart-link" className="relative">
              <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
              {count > 0 && (
                <span
                  data-testid="cart-count"
                  className="absolute -top-2 -right-2 bg-[#5B2A86] text-foreground text-[10px] font-mono w-4 h-4 flex items-center justify-center"
                >
                  {count}
                </span>
              )}
            </Link>
            <button
              className="md:hidden"
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((o) => !o)}
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {open && (
          <div className="md:hidden border-t border-border px-5 py-4 flex flex-col gap-4">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground"
              >
                {n.label}
              </Link>
            ))}
          </div>
        )}
      </header>

      <div className="border-b border-border bg-secondary py-2">
        <Marquee speed={22} gradient={false}>
          <span className="font-serif text-sm uppercase tracking-[0.3em] mx-8">
            MAJNUN — FORT WORTH, TEXAS ✦ FAITH IN THE DARK ✦ A WALK TO FAITH ✦ EST. TEXAS ✦
          </span>
          <span className="font-serif text-sm uppercase tracking-[0.3em] mx-8">
            MAJNUN — FORT WORTH, TEXAS ✦ FAITH IN THE DARK ✦ A WALK TO FAITH ✦ EST. TEXAS ✦
          </span>
        </Marquee>
      </div>

      <main className="flex-1">{children}</main>

      <footer data-testid="site-footer" className="border-t border-border bg-background">
        <div className="grid md:grid-cols-3 gap-10 px-5 md:px-10 py-14">
          <div>
            <img src={LOGO} alt="MAJNUN" className="h-16 w-auto object-contain mb-3" />
            <p className="text-xs font-mono text-muted-foreground leading-relaxed max-w-xs">
              A garment line born of struggle and devotion. Fort Worth, Texas. Made for the ones
              still walking toward the light.
            </p>
          </div>
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-4">
              Navigate
            </p>
            <div className="flex flex-col gap-2">
              {NAV.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  className="font-serif text-lg uppercase hover:text-[#5B2A86] transition-colors"
                >
                  {n.label}
                </Link>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-4">
              Join the procession
            </p>
            <form onSubmit={submitNewsletter} className="flex" data-testid="footer-newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                data-testid="newsletter-email-input"
                className="flex-1 bg-transparent border border-border px-3 py-2 text-xs font-mono focus:outline-none focus:border-foreground"
              />
              <button
                type="submit"
                data-testid="newsletter-submit"
                className="bg-foreground text-background px-4 text-xs font-mono uppercase tracking-widest hover:bg-[#5B2A86] hover:text-foreground transition-colors"
              >
                Join
              </button>
            </form>
          </div>
        </div>
        <div className="border-t border-border px-5 md:px-10 py-5 flex flex-col md:flex-row justify-between gap-2">
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            © {new Date().getFullYear()} MAJNUN. All rites reserved.
          </p>
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            Fort Worth, TX
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
